/**
 * Campaign → Brands & Discounts — /campaigns/:id/discounts
 *
 * Every brand scope of the Pricy campaign this campaign is linked to, read
 * live from Pricy: brand, discount, dates, markets, on/off. Switched-on
 * brands first; switched-off scopes (prefilled but not running) behind a
 * toggle. "Download Excel" exports every scope with its status.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useCampaign } from '../lib/api/campaigns';
import { fmtRange } from '../lib/api/blackFriday';
import { downloadPricyDiscountsXlsx, usePricyDiscounts } from '../lib/api/pricy';
import { BF_STYLES } from './blackFriday/styles';

const PAGE_STYLES = `
.bf .on { color: #1F6B3A; background: #E3F4E8; }
.bf .off { color: var(--ink-4); background: var(--bg-subtle); }
.bf tr.is-off td { color: var(--ink-4); }
.bf .disc { font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
`;

const errorText = (err: unknown) => {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || 'Could not read the brands from Pricy.';
};

const CampaignDiscounts = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaign = useCampaign(id);
  const linked = !!campaign.data?.pricy_campaign_id;
  const discounts = usePricyDiscounts(id, linked);
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const c = campaign.data;
  const data = discounts.data;
  const needle = q.trim().toLowerCase();
  const rows = (data?.brands ?? []).filter((r) => {
    if (!showOff && !r.enabled) return false;
    if (needle && !`${r.brand} ${r.scope}`.toLowerCase().includes(needle)) return false;
    return true;
  });

  const download = async () => {
    if (!id || !c) return;
    setDownloading(true);
    setDownloadError('');
    try {
      await downloadPricyDiscountsXlsx(id, c.name);
    } catch {
      setDownloadError('The Excel download failed — try again in a moment.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bf">
      <style>{BF_STYLES + PAGE_STYLES}</style>
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="link" onClick={() => navigate(`/campaigns/${id}`)} style={{ fontSize: 12 }}>
          ← Back to {c?.name ?? 'the campaign'}
        </button>
      </div>

      <div className="page-header">
        <div>
          <div className="page-eyebrow">{c?.name ?? 'Campaign'}</div>
          <h1 className="page-title">Brands &amp; Discounts</h1>
          {data && (
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              From Pricy #{data.pricy_campaign_id}
              {data.pricy_campaign.status ? ` · ${data.pricy_campaign.status}` : ''}
              {data.pricy_campaign.start_date ? ` · ${fmtRange(data.pricy_campaign.start_date, data.pricy_campaign.end_date)}` : ''}
              {' · '}
              <a href={data.pricy_campaign_url} target="_blank" rel="noreferrer">Open in Pricy ↗</a>
            </div>
          )}
        </div>
        {data && (
          <Button variant="primary" onClick={download} disabled={downloading}>
            {downloading ? 'Preparing…' : '⬇ Download Excel'}
          </Button>
        )}
      </div>

      {campaign.isLoading ? (
        <Card><div className="empty">Loading…</div></Card>
      ) : !c ? (
        <Card><div className="empty">Campaign not found.</div></Card>
      ) : !linked ? (
        <Card>
          <div className="empty">
            This campaign isn't linked to a Pricy campaign yet, so there are no brands and discounts to show.
          </div>
        </Card>
      ) : (
        <>
          <div className="toolbar">
            <input className="input" style={{ maxWidth: 260 }} placeholder="Search brand…" value={q} onChange={(e) => setQ(e.target.value)} />
            <button type="button" className={`filter-pill ${showOff ? 'active' : ''}`} onClick={() => setShowOff((v) => !v)}>
              Show switched-off{data ? ` (${data.counts.off})` : ''}
            </button>
            {data && (
              <span className="muted small" style={{ marginLeft: 'auto' }}>
                {data.counts.on} brand{data.counts.on === 1 ? '' : 's'} on · {data.counts.off} off
              </span>
            )}
          </div>
          {downloadError && <div className="muted small" style={{ color: '#9A2B22', marginBottom: 8 }}>{downloadError}</div>}
          <Card>
            {discounts.isLoading ? (
              <div className="empty">Reading the brands from Pricy…</div>
            ) : discounts.isError ? (
              <div className="empty">{errorText(discounts.error)}</div>
            ) : rows.length === 0 ? (
              <div className="empty">
                No brands match{needle ? <> “{q.trim()}”</> : ''}.
                {!showOff && data && data.counts.off > 0 ? ' Switched-off brands are hidden.' : ''}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>Discount</th>
                      <th>Status</th>
                      <th>Dates</th>
                      <th>Markets</th>
                      <th>Pricy scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.scope_id} className={r.enabled ? '' : 'is-off'}>
                        <td><span className="brand-name">{r.brand}</span></td>
                        <td>
                          <span className="disc">{r.discount || '—'}</span>
                          {r.discount_type && r.discount_type !== 'Relative' && (
                            <span className="muted small"> {r.discount_type}</span>
                          )}
                        </td>
                        <td><span className={`pill ${r.enabled ? 'on' : 'off'}`}>{r.enabled ? 'On' : 'Off'}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtRange(r.start_date, r.end_date) || '—'}</td>
                        <td>{r.markets.length ? r.markets.join(', ') : '—'}</td>
                        <td className="muted">{r.scope !== r.brand ? r.scope : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default CampaignDiscounts;
